import {
  DEFAULT_SEON_CONFIG,
  getSeonConfigForFeature,
  isFeatureDisabled,
  isSeonEnabled,
  SEON_FEATURE,
} from "@configs/seon";
import useServerInfo from "@hooks/useServerInfo";
import seon, { SDKInitOptions, SDKOptions } from "@seontechnologies/seon-javascript-sdk";
import logger from "@utils/logger";
import { useCallback, useEffect, useState } from "react";

const DEFAULT_INIT_CONFIG: SDKInitOptions = {};

/**
 * Get seon session id with default config
 * Returns undefined if the operation takes longer than 4000ms
 */
const getSeonSession = async (config?: SDKOptions) => {
  try {
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error("Seon session timeout")), 4000);
    });

    const seonSessionPromise = seon.getSession({ ...DEFAULT_SEON_CONFIG, ...(config ?? {}) });

    try {
      return await Promise.race([seonSessionPromise, timeoutPromise]);
    } catch (error) {
      // Ensure the losing promise doesn't cause unhandled rejection
      seonSessionPromise.catch(() => {});
      timeoutPromise.catch(() => {});
      throw error;
    }
  } catch (error) {
    logger._console.log("Error getting seon session id", error);
    return undefined;
  }
};

/**
 * Initialize seon with default config
 */
const initSeon = (config?: SDKInitOptions) => {
  try {
    seon.init({ ...DEFAULT_INIT_CONFIG, ...(config ?? {}) });
  } catch (error) {
    logger._console.log("Error initializing seon", error);
  }
};

interface SeonSessionConfigOptions {
  /**
   * Automatically initialize seon when the component mounts
   */
  autoInit?: boolean;
  /**
   * Initialize seon with the given config
   */
  initConfig?: SDKInitOptions;
  /**
   * Automatically get session when the component mounts
   * Session will be set to seonSession
   */
  autoGetSession?: boolean;
}

/**
 * Seon session hook to init and get session for a specific feature
 * A wrapper for seon javascript sdk, customized to work with React
 * - Use options.autoInit to automatically initialize seon when the component mounts
 * - Use getSession to get the session before sending payload to backend
 *
 * @returns
 * - isFetching: boolean - whether the session is being fetched
 * - getSession: function to get the session - will return a promise that resolves to the session
 * - seonSession: string | undefined - the session if it has been fetched
 */
export const useSeonSession = (feature: SEON_FEATURE, options?: SeonSessionConfigOptions) => {
  const [isFetching, setIsFetching] = useState(false);
  const [seonSession, setSeonSession] = useState<string | undefined>(undefined);
  const serverInfo = useServerInfo();

  useEffect(() => {
    if (options?.autoInit) {
      // Primary check: Skip initialization if SEON is disabled
      if (!isSeonEnabled(serverInfo)) {
        logger._console.log(`[Seon] Skipped initialization for feature: ${feature} - disabled`);
        return;
      }
      // Skip initialization if feature is disabled
      if (isFeatureDisabled(feature)) {
        logger._console.log(`[Seon] Skipped initialization for feature: ${feature} - disabled`);
        return;
      }
      initSeon(options?.initConfig);
      logger._console.log(`[Seon] Initialized feature: ${feature}`);
    }
  }, [options?.autoInit, options?.initConfig, feature, serverInfo]);

  const getSession = useCallback(
    async (config?: SDKOptions) => {
      // Primary check: Skip getting session if SEON is disabled
      if (!isSeonEnabled(serverInfo)) {
        logger._console.log(`[Seon] Skipped getting session for feature: ${feature} - disabled`);
        return undefined;
      }
      // Skip getting session if feature is disabled
      if (isFeatureDisabled(feature)) {
        logger._console.log(`[Seon] Skipped getting session for feature: ${feature} - disabled`);
        return undefined;
      }
      logger._console.log(`[Seon] Getting session for feature: ${feature}`);
      setIsFetching(true);

      try {
        // Merge feature-specific config with provided config (provided config takes precedence)
        const featureConfig = getSeonConfigForFeature(feature);
        const mergedConfig = { ...featureConfig, ...(config ?? {}) };

        const session = await getSeonSession(mergedConfig);
        setSeonSession(session);
        return session;
      } finally {
        setIsFetching(false);
      }
    },
    [feature, serverInfo],
  );

  useEffect(() => {
    if (options?.autoGetSession) {
      getSession()
        .then((session) => {
          setSeonSession(session);
        })
        .catch(() => {
          logger._console.log(`[Seon] Failed to auto-get session for feature: ${feature}`);
        });
    }
  }, [options?.autoGetSession, getSession, feature]);

  return {
    isFetching,
    getSession,
    seonSession,
  };
};
